import { ArrowIcon } from '../components/ArrowIcon'
import { motion, useScroll, useTransform } from 'motion/react'
import { Link } from 'react-router-dom'
import { lazy, Suspense, useRef, type CSSProperties } from 'react'
import { ResponsivePicture } from '../components/ResponsivePicture'
import { trip } from '../data/itinerary'
import { assetBySource } from '../lib/media'

const InkMap = lazy(() => import('../components/InkMap').then((module) => ({ default: module.InkMap })))

export function HomePage() {
  const page = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: page, offset: ['start start', 'end end'] })
  const heroY = useTransform(scrollYProgress, [0, .18], [0, 130])
  const heroOpacity = useTransform(scrollYProgress, [0, .13], [1, .2])

  return (
    <main id="main" ref={page} className="journey-page">
      <section className="hero" aria-labelledby="hero-title">
        <motion.div className="hero-copy" style={{ y: heroY, opacity: heroOpacity }}>
          <p className="kicker"><span>旅程</span> A nine-day passage</p>
          <h1 id="hero-title">Japan,<br /><em>in living ink.</em></h1>
          <p className="hero-deck">Tokyo’s electric edges. Kyoto’s quiet grain. One line west, drawn day by day.</p>
          <a className="journey-cta" href="#days"><span>Enter the journey</span><b aria-hidden="true"><ArrowIcon direction="down" /></b></a>
        </motion.div>
        <Suspense fallback={<div className="ink-map map-loading" aria-hidden="true" />}>
          <InkMap progress={scrollYProgress} />
        </Suspense>
        <div className="hero-date"><span>YYC</span><i>—</i><span>TYO</span><i>—</i><span>KYO</span><small>{trip.dateRange} · JST</small></div>
        <div className="vertical-note" aria-hidden="true">東京から京都へ · 二〇二六年</div>
      </section>

      <section className="prologue" aria-label="Journey introduction">
        <p className="chapter-number">00</p>
        <div>
          <p className="kicker">Before the first step</p>
          <h2>Two cities.<br />One unfolding line.</h2>
        </div>
        <p className="prologue-copy">Arrive gently. Move with the city instead of through it. Leave room for whatever we didn’t plan to find.</p>
      </section>

      <div id="days" className="day-index">
        {trip.days.map((day, index) => {
          const cover = assetBySource(day.coverSource)
          const reverse = index % 2 === 1
          return (
            <article
              key={day.slug}
              className={`day-teaser ${reverse ? 'reverse' : ''} ${!cover ? 'text-only' : ''}`}
              style={{ '--day-index': index } as CSSProperties}
            >
              <div className="day-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</div>
              {cover && (
                <motion.div
                  className="day-image"
                  initial={{ opacity: 0, y: 48 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-12%' }}
                  transition={{ duration: .78, ease: [.16, 1, .3, 1] }}
                >
                  <ResponsivePicture
                    asset={cover}
                    sizes="(max-width: 720px) 92vw, 58vw"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                  <span className="image-stamp">{day.city}</span>
                </motion.div>
              )}
              <motion.div
                className="day-copy"
                initial={{ opacity: 0, x: reverse ? 28 : -28 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-15%' }}
                transition={{ duration: .68, ease: [.16, 1, .3, 1], delay: .08 }}
              >
                <p className="day-meta"><span>{day.date}</span>{day.weekday} · {day.city}</p>
                <p className="kicker">{day.eyebrow}</p>
                <h2>{day.title}</h2>
                <p>{day.summary}</p>
                <Link className="text-link" to={`/day/${day.slug}`} viewTransition>
                  Open day <span aria-hidden="true"><ArrowIcon /></span>
                </Link>
              </motion.div>
            </article>
          )
        })}
      </div>

      <section className="epilogue">
        <span className="epilogue-sun" aria-hidden="true" />
        <p className="kicker">The route is set</p>
        <h2>Leave room<br />for wonder.</h2>
        <Link to="/wallet" className="journey-cta"><span>Open travel wallet</span><b aria-hidden="true"><ArrowIcon /></b></Link>
      </section>
    </main>
  )
}
